import { CameraGestures } from './render/camera-gestures';
import {
  MAX_PRIMARY_CAPACITY,
} from './simulation/config';
import { CosmicRenderer } from './render/cosmic-renderer';
import { RendererState } from './render/renderer-state';
import {
  createPrimaryTransferBuffer,
  type MainToWorkerMessage,
  type WorkerToMainMessage,
  viewPrimaryTransferBuffer,
} from './worker/protocol';
import {
  SettingsController,
  type SettingsSnapshot,
} from './settings/settings-controller';
import { RENDER_TARGET_FPS } from './timing';
import type { CometCargo } from './simulation/comet-recovery';
import { ClimateInspector } from './render/climate-inspector';

export class ParticleFieldApp {
  private readonly events = new AbortController();
  private readonly climateInspector = new ClimateInspector();
  private readonly canvas = requireElement<HTMLCanvasElement>('cosmos');
  private readonly cometEffects = requireElement<HTMLElement>('comet-effects');
  private readonly state = new RendererState(MAX_PRIMARY_CAPACITY);
  private readonly renderer: CosmicRenderer;
  private readonly worker: Worker;
  private readonly settings: SettingsController;
  private settingsSnapshot: SettingsSnapshot = {
    paused: false,
  };
  private readonly cameraGestures = new CameraGestures();
  private readonly activeCometImpacts: Array<{
    readonly element: HTMLElement;
    readonly x: number;
    readonly y: number;
    readonly startedAt: number;
    readonly bodyIndex: number;
    readonly offsetX: number;
    readonly offsetY: number;
  }> = [];
  private animationFrame = 0;
  private lastRenderAt = performance.now();
  private nextRenderAt = 0;
  private fpsWindowStartedAt = performance.now();
  private fpsWindowFrameCount = 0;
  private renderSequence = 0;
  private lastCameraDomUpdateAt = 0;
  private running = true;
  private currentSeed: number;
  private readonly renderInput = {
    deltaSeconds: 0,
    interpolation: 1,
  };

  public constructor() {
    let renderer: CosmicRenderer | undefined;
    let worker: Worker | undefined;
    let settings: SettingsController | undefined;
    try {
    this.currentSeed = seedFromLocation();
    this.renderer = renderer = new CosmicRenderer(
      this.canvas,
      this.state,
    );
    this.worker = worker = new Worker(
      new URL('./worker/simulation.worker.ts', import.meta.url),
      { type: 'module', name: 'cosmic-primary-simulation' },
    );
    this.worker.addEventListener('message',
(event: MessageEvent<WorkerToMainMessage>) =>
        this.onWorkerMessage(event.data),
{ signal: this.events.signal }
    );
    this.worker.addEventListener('error',
(event) => {
      event.preventDefault();
      this.showFallback(event.message || 'The simulation worker stopped unexpectedly.');
    },
{ signal: this.events.signal });
    this.settings = settings = new SettingsController({
      onChange: (snapshot) => this.applySettings(snapshot),
      onRestart: () => this.restart(),
      onFireComet: (cargo) => this.fireComet(cargo),
    });
    this.settingsSnapshot = this.settings.snapshot();

    const buffers = [
      createPrimaryTransferBuffer(MAX_PRIMARY_CAPACITY),
      createPrimaryTransferBuffer(MAX_PRIMARY_CAPACITY),
    ] as const;
    const initMessage: MainToWorkerMessage = {
      type: 'init',
      seed: this.currentSeed,
      buffers,
    };
    this.worker.postMessage(initMessage, [...buffers]);

    document.addEventListener('visibilitychange',
() => {
      this.state.resetInterpolation();
      this.clearCometImpacts();
      this.post({ type: 'control', hidden: document.hidden });
      document.body.dataset.visibility = document.hidden ? 'hidden' : 'visible';
      this.lastRenderAt = performance.now();
      this.nextRenderAt = 0;
      this.fpsWindowStartedAt = this.lastRenderAt;
      this.fpsWindowFrameCount = 0;
    },
{ signal: this.events.signal });
    window.addEventListener('resize',
() => this.resize(),
{ ...{ passive: true }, signal: this.events.signal });
    this.canvas.addEventListener('webglcontextlost',
(event) => {
      event.preventDefault();
      this.showFallback('The graphics context was lost. Reload to restart.');
    },
{ signal: this.events.signal });
    this.canvas.addEventListener('click',
(event) => {
      if (event.button !== 0) {
        return;
      }
      if (this.cameraGestures.consumeClick()) {
        return;
      }
      if (this.renderer.toggleCameraAt(event.clientX, event.clientY)) {
        this.updateCameraState(performance.now(), true);
      }
    },
{ signal: this.events.signal });
    this.canvas.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      this.cameraGestures.down(event.pointerId, event.clientX, event.clientY);
      this.canvas.setPointerCapture(event.pointerId);
      this.canvas.classList.add('camera-dragging');
    }, { signal: this.events.signal });
    this.canvas.addEventListener('pointermove', (event) => {
      const gesture = this.cameraGestures.move(event.pointerId, event.clientX, event.clientY);
      if (!gesture) return;
      if (gesture.type === 'pan') this.renderer.panCamera(gesture.dx, gesture.dy);
      else this.renderer.pinchCamera(gesture.ratio, gesture.from.x, gesture.from.y,
        gesture.to.x, gesture.to.y);
      this.updateCameraState(performance.now(), true);
      event.preventDefault();
    }, { signal: this.events.signal });
    for (const type of ['pointerup', 'pointercancel', 'lostpointercapture'] as const) {
      this.canvas.addEventListener(type, (event) => {
        this.cameraGestures.up(event.pointerId);
        if (this.canvas.hasPointerCapture(event.pointerId)) this.canvas.releasePointerCapture(event.pointerId);
        if (!this.cameraGestures.active) this.canvas.classList.remove('camera-dragging');
      }, { signal: this.events.signal });
    }
    this.canvas.addEventListener('wheel',
(event) => {
        event.preventDefault();
        const unit = event.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? 16
          : event.deltaMode === WheelEvent.DOM_DELTA_PAGE ? this.canvas.clientHeight : 1;
        this.renderer.zoomCamera(event.deltaY * unit);
        this.updateCameraState(performance.now(), true);
      },
{ ...{ passive: false }, signal: this.events.signal }
    );

    document.body.dataset.visibility = document.hidden ? 'hidden' : 'visible';
    document.body.dataset.renderTargetFps = String(RENDER_TARGET_FPS);
    document.body.dataset.renderFps = '0.0';
    this.resize();
    this.applySettings(this.settingsSnapshot);
    this.post({ type: 'control', hidden: document.hidden });
    document.body.dataset.renderer = 'ready';
    this.updateCameraState(performance.now(), true);
    this.animationFrame = requestAnimationFrame((now) => this.animate(now));
    } catch (error) {
      this.running = false;
      this.events.abort();
      settings?.dispose();
      worker?.terminate();
      renderer?.dispose();
      cancelAnimationFrame(this.animationFrame);
      throw error;
    }
  }

  private onWorkerMessage(message: WorkerToMainMessage): void {
    if (!this.running) {
      return;
    }
    if (message.type === 'ready') {
      document.body.dataset.worker = 'ready';
      document.body.dataset.simulationFps = String(message.simulationFps);
      document.dispatchEvent(new CustomEvent('cosmic:ready'));
      return;
    }
    if (message.type === 'state') {
      this.climateInspector.accept(message.simulatedSeconds, message.climateTelemetry, message.step === 0);
      if (document.hidden) {
        this.post({ type: 'recycle', buffer: message.buffer }, [message.buffer]);
        return;
      }
      const views = viewPrimaryTransferBuffer(
        message.buffer,
        MAX_PRIMARY_CAPACITY,
      );
      if (message.step === 0) {
        this.state.resetInterpolation();
      }
      this.state.accept(
        views,
        message.count,
        message.simulatedSeconds,
        performance.now(),
        message.planetaryAlgaeCoverage,
        message.planetaryVegetation,
        message.wallDeltaSeconds,
        message.celestialCount,
        message.planetaryClimate,
      );
      this.renderer.updatePrimary();
      // RendererState owns CPU copies and updatePrimary has completed the GPU
      // upload, so the worker can reuse this transfer buffer immediately. The
      // simulation clock must not wait for the next animation frame.
      this.post({ type: 'recycle', buffer: message.buffer }, [message.buffer]);
      document.body.dataset.simulationStepMs =
        message.stepDurationMs.toFixed(2);
      document.body.dataset.simulationDeltaMs =
        (message.wallDeltaSeconds * 1_000).toFixed(2);
      document.body.dataset.simulationStepDeltaMs =
        (message.simulationDeltaSeconds * 1_000).toFixed(2);
      document.body.dataset.simulationActualFps =
        (1 / message.wallDeltaSeconds).toFixed(2);
      document.body.dataset.state = 'received';
      const sequence = Number.parseInt(
        document.body.dataset.stateSequence ?? '0',
        10,
      );
      document.body.dataset.stateSequence = String(
        Number.isFinite(sequence) ? sequence + 1 : 1,
      );
      return;
    }
    if (message.type === 'comet-status') {
      document.body.dataset.cometLaunch = message.launched
        ? 'launched'
        : 'blocked';
      return;
    }
    if (message.type === 'comet-impact') {
      if (document.hidden) {
        return;
      }
      this.spawnCometImpact(
        message.x,
        message.y,
        message.strength,
        performance.now(),
        message.bodyIndex ?? -1,
        message.offsetX ?? 0,
        message.offsetY ?? 0,
      );
      return;
    }
    if (message.type === 'worker-error') {
      this.showFallback(message.reason);
    }
  }

  private applySettings(snapshot: SettingsSnapshot): void {
    this.settingsSnapshot = snapshot;
    this.post({
      type: 'control',
      paused: snapshot.paused,
    });
    this.lastRenderAt = performance.now();
    this.nextRenderAt = 0;
  }

  private animate(now: number): void {
    if (!this.running) {
      return;
    }
    this.animationFrame = requestAnimationFrame((next) => this.animate(next));
    if (document.hidden) {
      this.lastRenderAt = now;
      return;
    }
    const frameInterval = 1000 / RENDER_TARGET_FPS;
    if (this.nextRenderAt === 0) {
      this.nextRenderAt = now;
    }
    if (now + 0.4 < this.nextRenderAt) {
      return;
    }
    const interval = Math.min(1_000, Math.max(0, now - this.lastRenderAt));
    this.lastRenderAt = now;
    this.nextRenderAt += frameInterval;
    if (now - this.nextRenderAt > frameInterval * 2) {
      this.nextRenderAt = now + frameInterval;
    }
    this.renderInput.deltaSeconds = interval / 1000;
    this.renderInput.interpolation = this.state.interpolation(now, true);
    this.renderer.render(this.renderInput);
    this.updateCometImpacts(now);
    this.updateCameraState(now);
    if (document.body.dataset.frame !== 'rendered') {
      document.body.dataset.frame = 'rendered';
    }
    if (document.body.dataset.renderPath !== this.renderer.renderPath) {
      document.body.dataset.renderPath = this.renderer.renderPath;
    }
    this.recordRenderedFrame(now);
  }

  private resize(): void {
    this.renderer.resize(window.innerWidth, window.innerHeight);
    this.updateCameraState(performance.now(), true);
  }

  private restart(): void {
    this.clearCometImpacts();
    this.currentSeed = randomSeed();
    this.post({ type: 'control', restartSeed: this.currentSeed });
    this.renderer.resetCamera();
    this.updateCameraState(performance.now(), true);
    const restarts = Number.parseInt(document.body.dataset.restarts ?? '0', 10);
    document.body.dataset.restarts = String(
      Number.isFinite(restarts) ? restarts + 1 : 1,
    );
  }

  private fireComet(cargo: CometCargo): void {
    document.body.dataset.cometLaunch = 'requested';
    document.body.dataset.cometCargo = cargo;
    this.post({ type: 'control', fireComet: true, cometCargo: cargo });
  }

  private spawnCometImpact(
    x: number,
    y: number,
    strength: number,
    now: number,
    bodyIndex: number,
    offsetX: number,
    offsetY: number,
  ): void {
    const element = document.createElement('div');
    element.className = 'comet-impact';
    element.style.setProperty(
      '--impact-scale',
      String(Math.max(0.8, Math.min(1.6, strength))),
    );
    this.cometEffects.append(element);
    this.activeCometImpacts.push({ element, x, y, startedAt: now, bodyIndex, offsetX, offsetY });
    const impacts = Number.parseInt(
      document.body.dataset.cometImpacts ?? '0',
      10,
    );
    document.body.dataset.cometImpacts = String(
      Number.isFinite(impacts) ? impacts + 1 : 1,
    );
    this.updateCometImpacts(now);
  }

  private updateCometImpacts(now: number): void {
    for (
      let index = this.activeCometImpacts.length - 1;
      index >= 0;
      index -= 1
    ) {
      const impact = this.activeCometImpacts[index];
      if (impact === undefined) {
        continue;
      }
      if (now - impact.startedAt >= 1_500) {
        impact.element.remove();
        this.activeCometImpacts.splice(index, 1);
        continue;
      }
      const screen = this.renderer.planetScreenPosition(impact.bodyIndex, impact.offsetX, impact.offsetY) ??
        this.renderer.worldScreenPosition(impact.x, impact.y);
      impact.element.style.left = `${screen.x}px`;
      impact.element.style.top = `${screen.y}px`;
    }
  }

  private clearCometImpacts(): void {
    this.cometEffects.replaceChildren();
    this.activeCometImpacts.length = 0;
  }

  private updateCameraState(now: number, force = false): void {
    if (!force && now - this.lastCameraDomUpdateAt < 100) {
      return;
    }
    this.lastCameraDomUpdateAt = now;
    const mode = this.renderer.cameraMode;
    this.climateInspector.show(mode);
    const zoom = this.renderer.cameraZoom.toFixed(3);
    const centerX = this.renderer.cameraCenterX.toFixed(5);
    const centerY = this.renderer.cameraCenterY.toFixed(5);
    if (document.body.dataset.camera !== mode) {
      document.body.dataset.camera = mode;
      document.body.classList.toggle('planet-focused', mode !== 'solar-system');
    }
    if (document.body.dataset.cameraZoom !== zoom) {
      document.body.dataset.cameraZoom = zoom;
    }
    if (document.body.dataset.cameraCenterX !== centerX) {
      document.body.dataset.cameraCenterX = centerX;
    }
    if (document.body.dataset.cameraCenterY !== centerY) {
      document.body.dataset.cameraCenterY = centerY;
    }
    const ariaLabel =
      mode === 'solar-system'
        ? 'Particle simulation. Drag to pan, pinch or use the mouse wheel to zoom, or click a celestial body to focus.'
        : 'Focused celestial body. Drag to pan away, pinch or use the mouse wheel to zoom, or click again to return.';
    if (this.canvas.getAttribute('aria-label') !== ariaLabel) {
      this.canvas.setAttribute('aria-label', ariaLabel);
    }
    for (let index = 1; index <= 16; index += 1) {
      const position = this.renderer.planetScreenPosition(index);
      if (position !== null) {
        const x = position.x.toFixed(1);
        const y = position.y.toFixed(1);
        if (this.canvas.dataset[`planet${index}X`] !== x) {
          this.canvas.dataset[`planet${index}X`] = x;
        }
        if (this.canvas.dataset[`planet${index}Y`] !== y) {
          this.canvas.dataset[`planet${index}Y`] = y;
        }
      }
    }
  }

  private recordRenderedFrame(now: number): void {
    this.renderSequence += 1;
    document.body.dataset.renderSequence = String(this.renderSequence);
    this.fpsWindowFrameCount += 1;
    const elapsed = now - this.fpsWindowStartedAt;
    if (elapsed < 1_000) {
      return;
    }
    document.body.dataset.renderFps = (
      (this.fpsWindowFrameCount * 1_000) /
      elapsed
    ).toFixed(1);
    this.fpsWindowStartedAt = now;
    this.fpsWindowFrameCount = 0;
  }

  private showFallback(reason: string): void {
    if (!this.running) {
      return;
    }
    this.running = false;
    this.events.abort();
    this.settings.dispose();
    this.clearCometImpacts();
    cancelAnimationFrame(this.animationFrame);
    this.worker.terminate();
    this.renderer.dispose();
    showStaticFallback(reason);
  }

  private post(
    message: MainToWorkerMessage,
    transfer: Transferable[] = [],
  ): void {
    if (this.running) {
      this.worker.postMessage(message, transfer);
    }
  }
}

export function showStaticFallback(reason?: string): void {
  document.body.classList.add('fallback-mode');
  requireElement<HTMLElement>('fallback').hidden = false;
  if (reason !== undefined) {
    const paragraph = requireElement<HTMLElement>('fallback').querySelector('p');
    if (paragraph !== null) paragraph.textContent = `The simulation could not start: ${reason}`;
  }
  document.body.dataset.fallback = 'visible';
}

function requireElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (element === null) {
    throw new Error(`Required application element #${id} is missing.`);
  }
  return element as T;
}

function seedFromLocation(): number {
  const parameter = new URLSearchParams(window.location.search).get('seed');
  if (parameter !== null) {
    const parsed = Number.parseInt(parameter, 10);
    if (Number.isFinite(parsed)) {
      return parsed >>> 0;
    }
  }
  return randomSeed();
}

function randomSeed(): number {
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  return values[0] ?? 1;
}
